import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { upsertEmployeeAccess, fetchAllEmployeeAccess } from "../../../Slices/Employeeaccessslice";
import { Card, Checkbox, Avatar, Tag, Typography, Space, Badge, Row, Col, message, Collapse, Empty, Spin } from "antd";
import { SearchOutlined, ApartmentOutlined, SaveOutlined, TeamOutlined, LoadingOutlined } from "@ant-design/icons";
import { InputField, AntdButton } from "../../../blueplanitUtils/InputField";

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Optimized utility functions
const extractDepartments = (nestedDatabucket) => nestedDatabucket[0]?.children[1]?.children?.map(dept => ({ name: dept.name })) || [];
const filterActiveEmployees = (emps) => emps?.filter((e) => e.employeestatus === "Active" && e.department?.trim()) || [];

export default function EmployeeManagement() {
  const dispatch = useDispatch();
  const { AllEmployees: allEmployees } = useSelector((s) => s.employee);
  const { NestedDatabucket } = useSelector((s) => s.dataBucket);
  const currentUser = useSelector((s) => s.user.user);
  const { saving, error, loadingAll, allEmployeeAccess } = useSelector((s) => s.employeeAccess);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [employeeAccess, setEmployeeAccess] = useState({});
  const [initialEmployeeAccess, setInitialEmployeeAccess] = useState({});
  const [isDirty, setIsDirty] = useState(false)

  // Memoized data processing
  const departments = useMemo(() => extractDepartments(NestedDatabucket), [NestedDatabucket]);
  const employees = useMemo(() => filterActiveEmployees(allEmployees), [allEmployees]);
  
  const activeDepartments = useMemo(() => 
    departments.map((d) => ({
      ...d,
      employeeCount: employees.filter((e) => e.department === d.name).length
    })).filter(d => d.employeeCount > 0),
    [departments, employees]
  );

  const employeesByDepartment = useMemo(() => {
    const grouped = {};
    employees.forEach((emp) => {
      if (!grouped[emp.department]) grouped[emp.department] = [];
      grouped[emp.department].push(emp);
    });
    return grouped;
  }, [employees]);

  // Load employee access data
  useEffect(() => {
    const loadEmployeeAccess = async () => {
      try {
        await dispatch(fetchAllEmployeeAccess({ includeInactive: false })).unwrap();
      } catch (error) {
        messageApi.error("Failed to load employee access data");
      }
    };
    loadEmployeeAccess();
  }, [dispatch, messageApi]);

  // Initialize employee access state
  useEffect(() => {
      if (employees.length === 0 || isDirty) return;
      const accessMap = {};
      employees.forEach((emp) => { accessMap[emp.id] = []; });
      
      if (allEmployeeAccess?.length > 0) {
        allEmployeeAccess.forEach((access) => {
          if (access.employees?.length) {
            accessMap[access.employeeId] = [...access.employees];
          }
        });
      }
      
      setEmployeeAccess(accessMap);
      setInitialEmployeeAccess(JSON.parse(JSON.stringify(accessMap)));
  }, [allEmployeeAccess, employees, isDirty]);

  // Optimized filtered employees with grouped structure
  const groupedFilteredEmployees = useMemo(() => {
    const filtered = !searchTerm ? employees : employees.filter((e) => {
      const lower = searchTerm.toLowerCase();
      return e.firstname?.toLowerCase().includes(lower) ||
             e.lastname?.toLowerCase().includes(lower) ||
             e.department?.toLowerCase().includes(lower);
    });

    const grouped = {};
    filtered.forEach((e) => {
      if (!grouped[e.department]) grouped[e.department] = [];
      grouped[e.department].push(e);
    });
    return grouped;
  }, [searchTerm, employees]);

  // Optimized access utility functions
  const getAccessCount = useCallback((empId) => 
    (employeeAccess[empId] || []).length, 
    [employeeAccess]
  );

  const hasAccess = useCallback((fromId, toId) => 
    (employeeAccess[fromId] || []).includes(toId), 
    [employeeAccess]
  );

  const hasDepartmentAccess = useCallback((fromId, deptName) => {
    const deptEmps = employeesByDepartment[deptName]?.filter(e => e.id !== fromId).map(e => e.id) || [];
    if (!deptEmps.length) return false;
    const current = employeeAccess[fromId] || [];
    return deptEmps.every((id) => current.includes(id));
  }, [employeesByDepartment, employeeAccess]);

  const hasDepartmentPartialAccess = useCallback((fromId, deptName) => {
    const deptEmps = employeesByDepartment[deptName]?.filter(e => e.id !== fromId).map(e => e.id) || [];
    if (!deptEmps.length) return false;
    const current = employeeAccess[fromId] || [];
    const hasAny = deptEmps.some((id) => current.includes(id));
    const hasAll = deptEmps.every((id) => current.includes(id));
    return hasAny && !hasAll;
  }, [employeesByDepartment, employeeAccess]);

  // Toggle individual employee access
  const toggleEmployeeAccess = useCallback((fromId, toId) => {
     setIsDirty(true);
    setEmployeeAccess((prev) => {
      const current = prev[fromId] || [];
      return {
        ...prev,
        [fromId]: current.includes(toId) 
          ? current.filter((id) => id !== toId) 
          : [...current, toId],
      };
    });
  }, []);

  // Toggle entire department access
  const toggleDepartmentAccess = useCallback((fromId, deptName) => {
     setIsDirty(true);
    const deptEmps = employeesByDepartment[deptName]?.filter(e => e.id !== fromId).map(e => e.id) || [];
    setEmployeeAccess((prev) => {
      const current = prev[fromId] || [];
      const hasAll = deptEmps.every((id) => current.includes(id));
      return {
        ...prev,
        [fromId]: hasAll 
          ? current.filter((id) => !deptEmps.includes(id)) 
          : [...new Set([...current, ...deptEmps])],
      };
    });
  }, [employeesByDepartment]);

  // Save changes
  const handleSave = useCallback(async () => {
    try {
      const updates = [];
      
      Object.keys(employeeAccess).forEach((empId) => {
        const current = employeeAccess[empId] || [];
        const initial = initialEmployeeAccess[empId] || [];
        const sorted = (arr) => [...arr].sort();
        
        if (JSON.stringify(sorted(current)) !== JSON.stringify(sorted(initial))) {
          const toAdd = current.filter((id) => !initial.includes(id));
          const toRemove = initial.filter((id) => !current.includes(id));
          const emp = employees.find((e) => e.id === empId);
          
          updates.push({
            employeeId: empId,
            employeeName: emp ? `${emp.firstname} ${emp.lastname}` : undefined,
            employeeDepartment: emp?.department,
            employeesToAdd: toAdd.length > 0 ? toAdd : undefined,
            employeesToRemove: toRemove.length > 0 ? toRemove : undefined,
            changedBy: currentUser?.email || currentUser?.username || 'system',
            note: 'Updated employee access permissions',
          });
        }
      });

      if (!updates.length) {
        messageApi.info("No changes to save");
        return;
      }

      messageApi.loading({ content: 'Saving employee access settings...', key: 'saving' });

      const results = await Promise.allSettled(
        updates.map((u) => dispatch(upsertEmployeeAccess(u)).unwrap())
      );

      const successful = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      if (!failed) {
        messageApi.success({ 
          content: `Successfully saved access settings for ${successful} employee(s)`, 
          key: 'saving',
          duration: 3
        });
        setIsDirty(false);
        setInitialEmployeeAccess(JSON.parse(JSON.stringify(employeeAccess)));
        await dispatch(fetchAllEmployeeAccess({ includeInactive: false })).unwrap();
      } else {
        messageApi.warning({ 
          content: `Saved ${successful} employee(s), ${failed} failed`, 
          key: 'saving',
          duration: 5
        });
      }
    } catch (err) {
      messageApi.error({ 
        content: error || "Failed to save employee access settings", 
        key: 'saving',
        duration: 5
      });
    }
  }, [employeeAccess, initialEmployeeAccess, employees, dispatch, messageApi, currentUser, error]);

  const selectedEmp = useMemo(() => 
    employees.find((e) => e.id === selectedEmployee),
    [employees, selectedEmployee]
  );

 if (loadingAll && Object.keys(employeeAccess).length === 0) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin 
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} 
          tip="Loading employee access data..." 
          size="large" 
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {contextHolder}

      <Header />

      <Row gutter={20}>
        <Col span={8}>
          <EmployeeList
            groupedEmployees={groupedFilteredEmployees}
            selectedEmployee={selectedEmployee}
            getAccessCount={getAccessCount}
            onEmployeeSelect={setSelectedEmployee}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </Col>

        <Col span={16}>
          <EmployeeAccessPanel
            selectedEmployee={selectedEmp}
            selectedEmployeeId={selectedEmployee}
            activeDepartments={activeDepartments}
            employeesByDepartment={employeesByDepartment}
            getAccessCount={getAccessCount}
            hasAccess={hasAccess}
            hasDepartmentAccess={hasDepartmentAccess}
            hasDepartmentPartialAccess={hasDepartmentPartialAccess}
            onToggleEmployeeAccess={toggleEmployeeAccess}
            onToggleDepartmentAccess={toggleDepartmentAccess}
          />
        </Col>
      </Row>

      <SaveFooter onSave={handleSave} saving={saving} />
    </div>
  );
}

// Optimized Sub-components
const Header = React.memo(() => (
  <div style={styles.headerRow}>
    <div>
      <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
        Employee Access Management
      </Title>
      <Text type="secondary">Manage which employees can access other employees' information</Text>
    </div>
  </div>
));

const EmployeeList = React.memo(({ groupedEmployees, selectedEmployee, getAccessCount, onEmployeeSelect, searchTerm, onSearchChange }) => (
  <Card 
    style={styles.listCard} 
    bodyStyle={{ padding: 16 }} 
    title={
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <TeamOutlined />
          <span>Select Employee</span>
        </div>
        <InputField
          placeholder="Search employees..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          style={{ width: "100%" }}
          size="middle"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
        />
      </div>
    }
  >
    <div style={{ maxHeight: 600, overflowY: "auto" }}>
      {Object.keys(groupedEmployees).length === 0 ? (
        <Empty description="No employees found" style={{ padding: "40px 0" }} />
      ) : (
        <Collapse ghost defaultActiveKey={Object.keys(groupedEmployees)}>
          {Object.keys(groupedEmployees).map((deptName) => (
            <Panel 
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong>{deptName}</Text>
                  <Badge 
                    count={groupedEmployees[deptName].length} 
                    style={{ backgroundColor: "#1677ff" }} 
                  />
                </div>
              } 
              key={deptName}
            >
              {groupedEmployees[deptName].map((emp) => (
                <EmployeeListItem 
                  key={emp.id} 
                  employee={emp} 
                  isSelected={selectedEmployee === emp.id} 
                  accessCount={getAccessCount(emp.id)} 
                  onClick={() => onEmployeeSelect(emp.id)} 
                />
              ))}
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  </Card>
));

const EmployeeAccessPanel = React.memo(({ 
  selectedEmployee, 
  selectedEmployeeId,
  activeDepartments, 
  employeesByDepartment,
  getAccessCount,
  hasAccess,
  hasDepartmentAccess,
  hasDepartmentPartialAccess,
  onToggleEmployeeAccess,
  onToggleDepartmentAccess
}) => (
  <Card 
    style={styles.detailCard} 
    bodyStyle={{ padding: 0 }} 
    title={
      selectedEmployee ? (
        <EmployeeHeader 
          employee={selectedEmployee} 
          accessCount={getAccessCount(selectedEmployeeId)} 
        />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ApartmentOutlined />
          <span>Employee Access</span>
        </div>
      )
    }
  >
    {selectedEmployeeId ? (
      <div style={{ maxHeight: 600, overflowY: "auto" }}>
        <Collapse ghost defaultActiveKey={[]} style={{ padding: "16px" }}>
          {activeDepartments.map((dept) => {
            const deptEmps = employeesByDepartment[dept.name]?.filter((e) => e.id !== selectedEmployeeId) || [];
            if (!deptEmps.length) return null;
            
            return (
              <Panel 
                header={
                  <DepartmentAccessHeader 
                    department={dept} 
                    employeeCount={deptEmps.length} 
                    hasAccess={hasDepartmentAccess(selectedEmployeeId, dept.name)} 
                    hasPartialAccess={hasDepartmentPartialAccess(selectedEmployeeId, dept.name)} 
                    onToggle={(e) => { 
                      e.stopPropagation(); 
                      onToggleDepartmentAccess(selectedEmployeeId, dept.name); 
                    }} 
                  />
                } 
                key={dept.name}
              >
                {deptEmps.map((emp) => (
                  <EmployeeAccessItem 
                    key={emp.id} 
                    employee={emp} 
                    hasAccess={hasAccess(selectedEmployeeId, emp.id)} 
                    onToggle={() => onToggleEmployeeAccess(selectedEmployeeId, emp.id)} 
                  />
                ))}
              </Panel>
            );
          })}
        </Collapse>
      </div>
    ) : (
      <Empty 
        description="Select an employee to manage their access" 
        style={{ padding: "60px 0" }} 
      />
    )}
  </Card>
));

const EmployeeListItem = React.memo(({ employee, isSelected, accessCount, onClick }) => (
  <div style={styles.employeeListItem(isSelected)} onClick={onClick}>
    <Space size={12}>
      <Avatar 
        size={32} 
        src={employee.image} 
        style={{ background: "#1677ff" }}
      >
        {employee.firstname?.charAt(0)}
      </Avatar>
      <div>
        <Text strong style={{ color: "inherit", display: "block" }}>
          {employee.firstname} {employee.lastname}
        </Text>
        <Text style={{ fontSize: 12, color: isSelected ? "rgba(255,255,255,0.8)" : "#8c8c8c" }}>
          {employee.designation || employee.role}
        </Text>
      </div>
    </Space>
    <Badge 
      count={accessCount} 
      style={{ 
        backgroundColor: isSelected ? "rgba(255,255,255,0.25)" : "#f0f0f0", 
        color: isSelected ? "#fff" : "#595959", 
        boxShadow: "none" 
      }} 
    />
  </div>
));

const EmployeeHeader = React.memo(({ employee, accessCount }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <Avatar 
      size={40} 
      src={employee.image} 
      style={{ background: "#1677ff" }}
    >
      {employee.firstname?.charAt(0)}
    </Avatar>
    <div>
      <div style={{ fontWeight: 600, fontSize: 15 }}>
        {employee.firstname} {employee.lastname}
      </div>
      <Text type="secondary" style={{ fontSize: 13 }}>
        {employee.designation || employee.role} • {employee.department}
      </Text>
    </div>
    <Tag color="blue" style={{ marginLeft: "auto" }}>
      {accessCount} Access
    </Tag>
  </div>
));

const DepartmentAccessHeader = React.memo(({ 
  department, 
  employeeCount, 
  hasAccess, 
  hasPartialAccess, 
  onToggle 
}) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Checkbox 
        checked={hasAccess} 
        indeterminate={hasPartialAccess} 
        onClick={onToggle} 
      />
      <ApartmentOutlined />
      <Text strong>{department.name}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        ({employeeCount} employees)
      </Text>
      {hasPartialAccess && (
        <Tag color="orange" style={{ fontSize: 11 }}>
          Partial Access
        </Tag>
      )}
    </div>
  </div>
));

const EmployeeAccessItem = React.memo(({ employee, hasAccess, onToggle }) => (
  <div style={styles.employeeAccessItem}>
    <Checkbox checked={hasAccess} onChange={onToggle}>
      <Space size={12}>
        <Avatar 
          size={28} 
          src={employee.image} 
          style={{ background: "#1677ff" }}
        >
          {employee.firstname?.charAt(0)}
        </Avatar>
        <div>
          <Text style={{ fontSize: 13 }}>
            {employee.firstname} {employee.lastname}
          </Text>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
            {employee.designation || employee.role}
          </Text>
        </div>
      </Space>
    </Checkbox>
  </div>
));

const SaveFooter = React.memo(({ onSave, saving }) => (
  <Card style={{ ...styles.mainCard, marginTop: 20 }}>
    <div style={styles.footer}>
      <Space>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1677ff" }} />
        <Text type="secondary">Changes will be saved to employee access permissions</Text>
      </Space>
      <AntdButton 
        type="primary" 
        size="large" 
        icon={<SaveOutlined />} 
        onClick={onSave} 
        loading={saving} 
        label="Save Access Settings" 
      />
    </div>
  </Card>
));

const styles = {
  container: { 
    minHeight: "100vh", 
    background: "#f5f5f5", 
    padding: "24px" 
  },
  headerRow: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "flex-start", 
    marginBottom: 24 
  },
  mainCard: { 
    borderRadius: 12, 
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)", 
    border: "none" 
  },
  listCard: { 
    borderRadius: 12, 
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)", 
    border: "none", 
    height: "fit-content" 
  },
  detailCard: { 
    borderRadius: 12, 
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)", 
    border: "none" 
  },
  employeeListItem: (isSelected) => ({ 
    padding: "12px 16px", 
    marginBottom: 8, 
    borderRadius: 8, 
    cursor: "pointer", 
    background: isSelected ? "#1677ff" : "#fff", 
    color: isSelected ? "#fff" : "#333", 
    border: "none", 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    transition: "all 0.2s" 
  }),
  employeeAccessItem: { 
    padding: "8px 0", 
    borderBottom: "1px solid #f0f0f0" 
  },
  footer: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center" 
  },
};